import { CompareLayersPopupArguments } from "../compare/types"
import { defineDirective, EditTarget, ExplorerContext, getTerraMapDynamicViewerOptions, groupBy, IOmniaShell, isAdmin, isArrayEmpty, isFunction, LayerViewModel, LmNode, LocaleString, models, partition, selectMany, TerramapDynamicLayerViewer } from "../omnia"
import { canCreateLayer, canEditLayer } from "../utils"
import { getLayerVisibilityToggleValue, viewExtent } from "./utils"

export class TreeCtrl {

    static create() {
        return defineDirective(TreeCtrl, "app/datalayers/menu/tree.html", {
            parent: "=",
            nodes: "=",
            level: "=",
            context: "="
        })
    }

    public parent: LmNode
    public nodes: LmNode[]
    public level: number
    public context: ExplorerContext

    static $inject = ["OmniaShell"]

    constructor(
        private shell: IOmniaShell,
        private readonly readOnly = shell.isReadOnly(p => p.mapLayers),
        private readonly permissions = shell.permissions.mapLayers,
        private readonly enablePoints = isAdmin(shell)) {
    }

    get map() {
        return this.context ? this.context.map : null
    }

    get activeLayer() {
        return (this.trackActiveLayer && this.context) ? this.context.activeLayer : null
    }

    get trackActiveLayer() {
        return this.context && this.context.trackActiveLayer
    }

    get trackActiveField() {
        return this.context && this.context.trackActiveField
    }


    getCssClass(n: LmNode) {

        return (n.class || "")
            + (" l" + this.level)
            + (this.isExpanded(n) ? " open" : "")
            + (n.layer ? "" : " folder")
            + (n.class == "field" ? ` field-${n.field.fieldId}` : "")
            + (n.class == "layer" ? ` layer-${n.layer.datalayerId}` : "")
            + (n.layer && n.layer.visible ? " visible-layer" : "")
            + (this.isActiveField(n) ? " active-field" : "")
            + (this.isActiveLayer(n) ? " active-layer" : "")

    }

    includeNode(node: LmNode) {

        if (node.filtered) return false

        const { parent } = this
        if (parent && parent.canSelectChildren && (!node.selectedFromParent)) {
            return false
        }

        return true

    }

    toggleNode(node: LmNode) {

        const layer = node.layer

        if (layer) {
            this.toggleLayer(node)
        }
        else {
            this.toggleGroupNode(node)
        }

        if (node.field && this.trackActiveField) {
            this.context.activeField = node.field
        }

    }

    toggleGroupNode(node: LmNode) {
        this.setExpanded(node, !node.expanded)
        this.context.removeActiveNode(node)
    }

    toggleVisibility(node: LmNode) {

        const visible = !node.isVisible()
        const { context } = this

        this.setRootVisibility(node, visible)

        if (visible) {
            this.viewExtent(node)
        }

        if (node.layer && this.trackActiveLayer) {
            context.activeLayer = visible
                ? node.layer
                : ((context.activeLayer == node.layer) ? null : context.activeLayer)
        }


    }

    toggleLayer(node: LmNode) {

        const { layer } = node
        const { map } = this

        const show = getLayerVisibilityToggleValue(layer, map) || (this.trackActiveLayer && (!this.isActiveLayer(node)))

        if (show) {
            this.setActiveLayer(node, true)
        }
        else {
            layer.visible = false
            if (this.isActiveLayer(node)) {
                this.context.activeLayer = null
            }
        }

        this.context.onLayerVisibilityChanged([layer], show)

    }

    toggleChildSelection(child: LmNode, select?: boolean) {

        let expanded: boolean

        if (!child.selectedFromParent) {
            this.setRootVisibility(child, false)
            expanded = false
        }
        else {
            expanded = true
        }

        this.setExpanded(child, expanded)

    }

    selectChildren(parent: LmNode, select: boolean) {

        parent.getChildren(true).forEach(c => {

            c.selectedFromParent = select

            if (!c.selectedFromParent) {
                this.setRootVisibility(c, false)
                this.setExpanded(c, false)
            }

        })


    }

    setActiveLayer(node: LmNode, viewExtent = true) {

        const { context } = this

        this.setRootVisibility(node, true)

        if (viewExtent) {
            this.viewExtent(node, false)
        }

        if (this.trackActiveLayer) {
            context.activeLayer = node.layer
        }

    }

    setRootVisibility(node: LmNode, visible: boolean) {

        const allChildren = this.setVisibility(node, visible)

        const layers = allChildren
            .map(c => c.layer)
            .filter(l => !!l)

        this.context.onLayerVisibilityChanged(layers, visible)

    }

    setVisibility(node: LmNode, visible: boolean): LmNode[] {

        if (node.layer) {
            node.layer.visible = visible
            return [node]
        }

        const children = (node.getChildren() || []).filter(c => !c.filtered)

        if (!children.length) {
            return []
        }

        let targets = children

        const [layers, others] = partition(children, c => !!c.layer)

        if (visible) {

            let show = this.context.canShowAnyNode
                ? layers.map(l => l.layer)
                : getOneLayerPerTarget(layers)

            show = show
                .filter(l => !!l)
                .filter(l => (!l.field) || l.field.visible)
                .filter(l => (!l.cropping) || l.cropping.visible)

            this.context.showLayers(show, true)

            targets = others

        }

        const results = selectMany(targets.map(c => this.setVisibility(c, visible)))

        this.setExpanded(node, visible)

        return layers.concat(results)

    }

    isVisible(node: LmNode) {
        return node.isVisible()
    }

    canShow(node: LmNode) {
        if (!node) return false
        if (node.canShow === "never") return false
        return node.canShow || (this.context && this.context.canShowAnyNode)
    }

    canViewExtent(node: LmNode) {
        return node && (!node.layer) && (node.field || (node.class == "field-group") || node.isVisible())
    }

    canCompareFieldLayers(node: LmNode) {
        return node && node.field && (node.class === "field")
    }

    compareLayers(node: LmNode) {

        const fieldId = node?.field?.fieldId

        if (fieldId) {

            const args: CompareLayersPopupArguments = {
                fieldId,
                fieldLayers: this.context.getLayers(l => l.fieldId === fieldId)
            }

            return this.shell.showComponentAsDialog("omnia-maplayer-compare-popup", args, { size: "full-screen" })

        }

    }

    viewExtent(node: LmNode, setActive = true) {

        const layer = viewExtent(this.map, node)

        if (layer && setActive) {
            this.setActiveLayer(node, false)
        }

    }

    canEditLayer(node: LmNode) {
        return (!this.context.readOnlyContext) && node && node.layer && isFunction(this.context.editLayer) && canEditLayer(node.layer)
    }

    canEditVariant(variant: LayerViewModel) {
        const { context, permissions } = this
        return (!context.readOnlyContext) && variant && permissions.canEditVariants
    }

    canCreateLayer(node: LmNode) {

        const { readOnly, context } = this

        if (readOnly || context.readOnlyContext) {
            return false
        }

        if (!isFunction(context.editLayer)) {
            return false
        }

        return canCreateLayer(node)

    }

    editLayer(node: LmNode | LayerViewModel) {
        this.context.editLayer(node, false)
    }

    editVariant(node: LmNode, variant: LayerViewModel) {

        const target: EditTarget = {
            field: node.field,
            layerType: variant.type,
            layer: variant,
        }

        this.context.editLayer(target, false)
    }

    createLayer(node: LmNode) {
        this.context.editLayer(node, true)
    }

    isActiveLayer(node: LmNode) {
        return this.trackActiveLayer
            ? node && node.layer && (node.layer == this.activeLayer) && node.layer.visible
            : false
    }

    isActiveField(node: LmNode) {
        const activeField = this.context ? this.context.activeField : null
        return node && node.fieldRoot && node.field && activeField && (activeField.fieldId == node.field.fieldId)
    }

    isExpanded(node: LmNode) {

        const { context } = this

        const activeNodes = (context ? context.activeNodes : undefined) || []

        if (node && (!node.layer) && (node.expanded || activeNodes.some(n => n == node))) {
            return true
        }

        if (node && context && context.autoExpandedVisible) {
            return node.isVisible()
        }
    }

    setExpanded(node: LmNode, expanded: boolean) {
        const { context } = this
        expanded = expanded || (context && context.autoExpandedVisible && node.isVisible())
        node.expanded = expanded
    }

    getTooltip(node: LmNode) {

        const { layer } = node

        if (layer) {

            let text = node.tooltip || layer.datalayer.Name || node.text

            const date = layer.date || layer.datalayer.CreatedOn

            if (date) {
                const dateText = date.toLocaleDateString()
                text = `${text} - ${dateText}`
            }

            if (this.shell.debug) {
                text = text + ` (#${node.layer.datalayerId})`
            }

            return text

        }

        return node.tooltip || node.text

    }

    showOpacitySlider(node: LmNode) {
        return (!this.context.hideOpacitySliders) && node && node.layer && node.isVisible()
    }

    showViewers(node: LmNode) {

        if (node && node.showViewers && node.isVisible()) {
            const v = this.context.viewManager.getViewers(node.layerType.TypeId)
            return v && (v.length > 1)
        }

    }

    canToggleLabels(node: LmNode) {

        if (node.layer) {
            return false
        }

        const layers = node.flatten(n => n.layer).filter(l => !!l)
        return layers.some(l => l.visible)

    }

    showLabels(node: LmNode) {

        if (node.layer) {
            return node.layer.showLabels
        }

        const layers = node.flatten(n => n.layer).filter(l => !!l)
        return layers.some(l => l.showLabels)
    }


    toggleLabels(node: LmNode) {

        if (node.layer) {
            node.layer.showLabels = !node.layer.showLabels
            return
        }

        const layers = node.flatten(n => n.layer).filter(l => !!l)
        return layers.forEach(l => l.showLabels = !l.showLabels)

    }

    canShowPoints(node: LmNode) {
        return this.enablePoints && node && node.layer.canShowPoints()
    }

    toggleShowPoints(node: LmNode) {
        node.layer.showPoints = !node.layer.showPoints
    }

    hasVariants(node: LmNode) {
        return node.layer && node.layer.hasVariants()
    }

    getVariants(node: LmNode) {
        return node.layer ? node.layer.getVariants() : undefined
    }

    showVariants(node: LmNode) {
        return this.hasVariants(node) && this.isActiveLayer(node) && node.showVariants
    }

    toggleShowVariants(node: LmNode) {

        const variants = this.getVariants(node)
        if (isArrayEmpty(variants)) return

        const show = node.showVariants = !node.showVariants

        if (!show) {
            variants.forEach(v => v.visible = false)
        }

    }

    onVariantVisibilityChanged(node: LmNode, variant: LayerViewModel) {
        this.context.onLayerVisibilityChanged([variant], variant.visible)
    }

    compareWithDynamicScale(node: LmNode, variant: LayerViewModel) {

        const type = node.layer.datalayer.Type
        if (!type) return

        const l2v = variant.datalayer.TerraMap != models.TerraMapLayerType.RawDynamicScale
            ? undefined
            : TerramapDynamicLayerViewer.factory(type.TypeId, type.Name, getTerraMapDynamicViewerOptions(type))

        const args: CompareLayersPopupArguments = {

            fieldId: node.field?.fieldId,
            croppingId: node.field?.croppingId,

            fieldLayers: [],

            compare: {
                layer1: {
                    layer: node.layer.datalayer,
                    name: "Predefined scale" as LocaleString
                },

                layer2: {
                    layer: variant.datalayer,
                    name: variant.variantDescription,
                    viewer: l2v
                }
            }

        }

        return this.shell.showComponentAsDialog("omnia-maplayer-compare-popup", args, { size: "full-screen" })

    }

}

function getOneLayerPerTarget(nodes: LmNode[]) {

    const layers = nodes.map(n => n.layer).filter(l => !!l)

    const groups = groupBy(layers, l => `${l.fieldId || 0}/${l.croppingId || 0}`)

    // 220117 - Usernap 2412
    // We are now assuming that all groups have been ordered upfront,
    // that the order has not been changed by the grouping operation above
    // and so simply retain the first item for each group
    const uniqueLayers = groups.map(g => g.values[0])

    return uniqueLayers

}
